class PincodeService {
    async getPincodeDetails(pincode) {
        try {
            // Validate pincode format (6 digits)
            if (!pincode || !/^\d{6}$/.test(pincode)) {
                throw new Error('Please enter a valid 6-digit pincode');
            }

            const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
            
            if (!response.ok) {
                throw new Error('Failed to fetch pincode details');
            }

            const data = await response.json();
            
            if (data && data.length > 0) {
                const result = data[0];
                
                if (result.Status === 'Success' && result.PostOffice && result.PostOffice.length > 0) {
                    // Prioritize post office selection for better city name
                    const mainPostOffice = this.selectMainPostOffice(result.PostOffice);

                    return {
                        success: true,
                        data: {
                            pincode: pincode,
                            city: mainPostOffice.Name,
                            district: mainPostOffice.District,
                            state: mainPostOffice.State,
                            country: mainPostOffice.Country,
                            region: mainPostOffice.Region,
                            division: mainPostOffice.Division,
                            circle: mainPostOffice.Circle,
                            block: mainPostOffice.Block,
                            postOffices: result.PostOffice.map(po => ({
                                name: po.Name,
                                branchType: po.BranchType,
                                deliveryStatus: po.DeliveryStatus,
                                district: po.District,
                                block: po.Block
                            }))
                        }
                    };
                } else {
                    return {
                        success: false,
                        message: result.Message || 'No postal information found for this pincode'
                    };
                }
            } else {
                return {
                    success: false,
                    message: 'Invalid response from postal service'
                };
            }
        } catch (error) {
            console.error('Error fetching pincode details:', error);
            return {
                success: false,
                message: error.message || 'Failed to fetch pincode details. Please check your internet connection.'
            };
        }
    }

    /**
     * Select the most appropriate post office for city name
     * Priority: Head Post Office > Delivery status > First available
     */
    selectMainPostOffice(postOffices) {
        // First priority: Head Post Office
        let selected = postOffices.find(po => 
            po.BranchType === 'Head Post Office'
        );

        // Second priority: Post office with delivery status
        if (!selected) {
            selected = postOffices.find(po => 
                po.DeliveryStatus === 'Delivery'
            );
        }

        // Third priority: Sub Post Office with delivery
        if (!selected) {
            selected = postOffices.find(po => 
                po.BranchType === 'Sub Post Office' && po.DeliveryStatus === 'Delivery'
            );
        }

        // Fourth priority: Any Sub Post Office
        if (!selected) {
            selected = postOffices.find(po => 
                po.BranchType === 'Sub Post Office'
            );
        }

        // Last resort: First available
        return selected || postOffices[0];
    }

    async validatePincode(pincode) {
        const result = await this.getPincodeDetails(pincode);
        return result.success;
    }

    formatPincodeResponse(postOffices) {
        if (!postOffices || postOffices.length === 0) {
            return null;
        }

        // Group by district and state for better organization
        const grouped = postOffices.reduce((acc, po) => {
            const key = `${po.district}, ${po.state}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(po.name);
            return acc;
        }, {});

        return grouped;
    }

    /**
     * Get all available cities/areas for a pincode
     */
    getCityOptions(pincodeData) {
        if (!pincodeData || !pincodeData.postOffices) {
            return [];
        }

        return pincodeData.postOffices.map(po => ({
            label: po.name,
            value: po.name,
            district: po.district,
            branchType: po.branchType,
            deliveryStatus: po.deliveryStatus
        }));
    }
}

export const pincodeService = new PincodeService(); 